import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AttachIcon from '../../../../assets/icons/AttachIcon';
import { editContentSubmission, getContentSubmissionPresignedUrl } from '../../../../services/dashboard.service';
import { ContentSubmission, MediaUrl } from '../../../../types/content-submissions';
import { MAX_IMAGES } from '../../../../utils/constants';
import ButtonComponent from '../../../common/button-component/button-component';
import CustomModal from '../../../common/custom-modal/custom-modal';
import InputComponent from '../../../common/input-component/input-component';
import UploadComponent from '../upload-component/upload-component';
import { validateAndFilterFiles } from '../upload-component/upload-utils';
import styles from './edit-resubmit-modal.module.css';

interface EditResubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  submission: ContentSubmission | null;
  isResubmit?: boolean;
}

export default function EditResubmitModal({ isOpen, onClose, submission, isResubmit = false }: EditResubmitModalProps) {
  const [contentName, setContentName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [hasVideo, setHasVideo] = useState(false);
  const queryClient = useQueryClient();

  // Update all relevant state when submission changes
  useEffect(() => {
    if (submission) {
      setContentName(submission.name);
      setLinkUrl(submission.type === 'link' ? (submission.urls[0] as string) : '');
      if (submission.type === 'media' && !isResubmit) {
        const mediaUrls = submission.urls as MediaUrl[];
        const files = mediaUrls.map((url) => {
          // Extract file name from URL
          const fileName = url.fileUrl.split('/').pop()?.split('?')[0] || 'file';
          // Create a File object with the URL and type information
          const file = new File([], fileName, { type: url.type });
          // Store the original URL as a property for reference
          Object.defineProperty(file, 'url', { value: url.fileUrl });
          Object.defineProperty(file, 'fileKey', { value: url.fileKey });
          return file;
        });
        setSelectedFiles(files);
        // Check if any of the files is a video
        const hasVideoFile = mediaUrls.some((url) => url.type.includes('video'));
        setHasVideo(hasVideoFile);
      } else {
        setSelectedFiles([]);
      }
    }
  }, [submission, isResubmit]);

  const handleClose = () => {
    setContentName('');
    setSelectedFiles([]);
    setLinkUrl('');
    setHasVideo(false);
    onClose();
  };

  // Mutation for editing submission
  const editOrResubmitMutation = useMutation({
    mutationFn: async () => {
      if (!submission?._id) throw new Error('No submission selected');

      // For media type submissions
      if (submission.type === 'media') {
        if (selectedFiles.length > 0) {
          const newFiles = selectedFiles?.filter(
            (file) =>
              !submission.urls.some((url) => {
                const selectedFileUrl = (url as MediaUrl).fileUrl;
                // Check if file has url property (added during our mapping process)
                return selectedFileUrl === (file as File & { url?: string }).url;
              })
          );

          // Handle file upload logic
          const fileNames = newFiles.map((file) => file.name);

          const presignedUrlResponse = await getContentSubmissionPresignedUrl(fileNames);

          if (!presignedUrlResponse.status) {
            throw new Error(presignedUrlResponse.message || 'Failed to get upload URLs');
          }

          const { urls } = presignedUrlResponse.data;
          const uploadedFiles = await Promise.all(
            newFiles.map(async (file, index) => {
              const fileUrl = urls[index];
              if (!fileUrl) {
                throw new Error(`No upload URL found for file ${file.name}`);
              }

              await fetch(fileUrl.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                  'Content-Type': file.type,
                },
              });

              return {
                key: fileUrl.key,
                type: file.type,
                name: file.name,
              };
            })
          );

          // Get old files that are still selected
          const oldFiles = selectedFiles
            .filter((file): file is File & { url: string; fileKey: string } => 'url' in file)
            .map((file) => {
              console.log(file);
              return {
                key: file.fileKey,
                type: file.type,
                name: file.name,
              };
            });

          // Combine old files and newly uploaded files
          const finalUploadedFiles = [...oldFiles, ...uploadedFiles];

          const response = await editContentSubmission(submission._id, {
            type: 'media',
            name: contentName,
            keys: finalUploadedFiles,
            link: null,
            isResubmit,
          });

          if (!response.status) {
            throw new Error(response.message || 'Failed to edit submission');
          }

          return response;
        }

        if (contentName !== submission.name) {
          // Only update name for media type
          const response = await editContentSubmission(submission._id, {
            type: 'media',
            name: contentName,
            keys: null,
            link: null,
            isResubmit: false,
          });

          if (!response.status) {
            throw new Error(response.message || 'Failed to edit submission');
          }

          return response;
        }
      }

      // For link type submissions
      if (submission.type === 'link') {
        const currentLink = typeof submission.urls[0] === 'string' ? submission.urls[0] : '';
        const response = await editContentSubmission(submission._id, {
          type: 'link',
          name: contentName,
          keys: null,
          link: linkUrl || currentLink,
          isResubmit,
        });

        if (!response.status) {
          throw new Error(response.message || 'Failed to edit submission');
        }

        return response;
      }

      throw new Error('No changes to save');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      toast.success('Submission edited successfully');
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error editing submission');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submission?._id) return;

    const hasChanges =
      contentName !== submission.name ||
      (submission.type === 'link' && linkUrl && linkUrl !== submission.urls[0]) ||
      (submission.type === 'media' && selectedFiles.length > 0);

    if (hasChanges) {
      editOrResubmitMutation.mutate();
    } else {
      toast.info('No changes to save');
    }
  };

  const handleFileSelection = (files: File[]) => {
    const { validFiles, newHasVideo } = validateAndFilterFiles(files, hasVideo, selectedFiles);

    if (validFiles.length === 0) return;

    if (newHasVideo) {
      setHasVideo(true);
      setSelectedFiles(validFiles); // Replace existing files with the video
      return;
    }

    // Handle image-only uploads
    if (selectedFiles.length + validFiles.length > MAX_IMAGES) {
      toast.warning(`You can upload only ${MAX_IMAGES} images. Some files were not added.`);
      const remainingSlots = MAX_IMAGES - selectedFiles.length;
      setSelectedFiles((prev) => [...prev, ...validFiles.slice(0, remainingSlots)]);
    } else {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    const fileToRemove = selectedFiles[index];
    if (fileToRemove.type.startsWith('video/')) {
      setHasVideo(false);
    }
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!isOpen || !submission) return null;

  return (
    <CustomModal
      show={isOpen}
      onClose={handleClose}
      title={isResubmit ? 'Resubmit Content' : 'Edit Submission'}
      centeredTitle="center"
      showDivider
      width={528}
    >
      <div className={styles.modalContent}>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <InputComponent
              id="contentName"
              label="Content Name"
              type="text"
              value={contentName}
              onChange={(e) => setContentName(e.target.value)}
              placeholder="Enter Content Name"
              required
            />
          </div>

          {submission.type === 'media' ? (
            <UploadComponent
              selectedFiles={selectedFiles}
              onFileSelection={handleFileSelection}
              hasVideo={hasVideo}
              maxImages={MAX_IMAGES}
              removeFile={removeFile}
            />
          ) : (
            <div className={styles.formGroup}>
              <InputComponent
                id="linkUrl"
                type="url"
                left={<AttachIcon />}
                label="Add Link"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Paste link here..."
                required
              />
            </div>
          )}

          <div className={styles.modalActions}>
            <ButtonComponent type="button" onClick={handleClose} variant="secondary" style={{ width: '100%' }}>
              Cancel
            </ButtonComponent>
            <ButtonComponent
              type="submit"
              className={styles.submitButton}
              disabled={
                !contentName ||
                (submission.type === 'media' && selectedFiles.length === 0) ||
                (submission.type === 'link' && !linkUrl)
              }
              showLoading={editOrResubmitMutation.isPending}
            >
              {isResubmit ? 'Submit' : 'Save'}
            </ButtonComponent>
          </div>
        </form>
      </div>
    </CustomModal>
  );
}
